import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Empty, Skeleton, Button as AntButton } from "antd";
import { ArrowLeft, Globe, AppWindow, ChevronRight, ExternalLink } from "lucide-react";
import { PageShell } from "../../components/PageShell";
import { Breadcrumb } from "../../components/Breadcrumb";
import { useDashTheme } from "../../hooks/useDashTheme";
import { Button } from "../../components/primitives";
import { DashAntd } from "../../components/DashAntd";
import { useApi } from "../../../../hooks/useApi";
import { useClassicOverview } from "../../hooks/useClassicOverview";
import { Spark, TrackBar, fmtMins, fmtH } from "../../components/classic/micro";
import { Prose, StretchAnalysis } from "../../components/drilldown/WorkLog";
import { DASHBOARD_BASE } from "../../../../config/featureFlags";
import {
  useClassicFilters, classicPath, personPath, toolPath, toolPersonPath, toolStretchPath, fmtDay,
} from "../../components/drilldown/drilldownRoutes";
import { Section, Crumbs, ScopeBar } from "./shared";
import { useDemoMode, DemoBanner, DemoHint, demoToolExtras } from "./demoData";
import { isGraphicalDrill } from "../graphical/drill/DrillFrame";
import { GraphicalTool } from "../graphical/drill/GraphicalTool";
import useEscapeBack from "../graphical/drill/useEscapeBack";

/**
 * ToolDrilldown — "what is this tool absorbing?", as pages.
 *
 * THE TOPIC IS THE TOOL, AND IT DOES NOT CHANGE ON THE WAY DOWN.
 * Clicking a person here opens that person INSIDE this tool — their hours in
 * it, the stretches of their work that name it — not their week, which is a
 * different subject. Leaving the tool is possible but always explicit and
 * labelled ("Open X's full drill-down"), never the result of drilling.
 *
 *   view="digest"   time in the tool, who used it, and what was DONE in it
 *   view="person"   one person, scoped to this tool
 *   view="stretch"  one stretch in full, keeping whatever trail led to it
 *
 * The analysis tier is never silently absent: where nothing written names the
 * tool, the page says so rather than rendering an empty panel. Raw capture is
 * never shown — every text here comes from the analysed layer.
 */
export function ToolDrilldown({ view = "digest" }) {
  const t = useDashTheme();
  const api = useApi();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { range, member } = useClassicFilters();

  const name = params.get("name") || "";
  const type = (params.get("type") || "").toLowerCase() === "website" ? "website" : "application";
  const category = params.get("category") || "";
  const tool = React.useMemo(() => ({ name, type, category }), [name, type, category]);

  const personId = params.get("u") || null;
  const personHint = params.get("pn") || "";
  const day = params.get("day") || null;
  const sess = params.get("sess") || null;
  const focus = view === "stretch" ? Number(params.get("i")) : null;

  const [raw, setRaw] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  /* Coverage is a disclosure, not decoration: per-tool breakdown only exists
     for people whose activity detail we hold. The dashboard states it, so the
     tool page has to as well rather than implying full coverage. */
  const classic = useClassicOverview(range, member);
  const coverage = classic.overview?.detail?.coverage || null;

  const startKey = range?.start ? fmtDay(range.start) : null;
  const endKey = range?.end ? fmtDay(range.end) : null;

  React.useEffect(() => {
    /* No tool in the URL — usually a hand-edited link that replaced the query
       instead of appending to it. Stop loading and let the empty state say so,
       rather than spinning a skeleton forever. */
    if (!name) { setLoading(false); setRaw({ empty: true }); return undefined; }
    let alive = true;
    setLoading(true); setRaw(null);
    const qs = new URLSearchParams({ name, type });
    if (startKey) qs.set("startDate", startKey);
    if (endKey) qs.set("endDate", endKey);
    if (member) qs.set("userId", member); // member dropdown scope; server-validated
    api.get(`/api/v1/classic-dashboard/tool-detail?${qs.toString()}`)
      .then((res) => { if (alive) setRaw(res.data?.data || null); })
      .catch(() => { if (alive) setRaw({ empty: true, degraded: true }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api, name, type, startKey, endKey, member]);

  /* Sample data FILLS gaps, never replaces what the server returned. Off unless
     the URL says ?demo=1 — see demoData.js. */
  const demo = useDemoMode();
  const data = React.useMemo(() => {
    if (!demo || !raw) return raw;
    const extras = demoToolExtras(name, raw.people || [], range);
    if (raw.empty) {
      return {
        __demo: true,
        totalMinutes: 566,
        sparkMinutes: [120, 95, 0, 140, 110, 60, 41],
        people: [
          { userId: "demo-u1", name: "Sample Person", position: "Sr. React Developer", minutes: 320 },
          { userId: "demo-u2", name: "Second Sample", position: "Jr. React Developer", minutes: 246 },
        ],
        stretches: demoToolExtras(name, [
          { userId: "demo-u1", name: "Sample Person" },
          { userId: "demo-u2", name: "Second Sample" },
        ], range).stretches,
        insights: extras.insights,
      };
    }
    const needStretches = !(raw.stretches || []).length;
    const needInsights = !(raw.insights || []).length;
    if (!needStretches && !needInsights) return raw;
    return {
      ...raw,
      __demo: true,
      stretches: needStretches ? extras.stretches : raw.stretches,
      insights: needInsights ? extras.insights : raw.insights,
    };
  }, [demo, raw, name, range]);

  const showingSample = !!data?.__demo;
  const allStretches = React.useMemo(() => data?.stretches || [], [data]);
  const people = data?.people || [];
  const maxPerson = Math.max(...people.map((p) => p.minutes), 1);

  const person = personId ? people.find((p) => String(p.userId) === String(personId)) : null;
  const personName = person?.name || personHint || "Member";

  /* Indices stay GLOBAL — into the tool's full stretch list — so a link to a
     stretch means the same thing whether it was opened from the tool digest or
     from inside one person. Only the display list is filtered. */
  const personStretchIdx = React.useMemo(
    () => (personId
      ? allStretches.map((s, i) => i).filter((i) => String(allStretches[i].userId) === String(personId))
      : []),
    [allStretches, personId]
  );

  const stretch = focus != null ? allStretches[focus] : null;

  const backToDigest = React.useCallback(() => navigate(toolPath(tool, range, member)), [navigate, tool, range, member]);
  const backToPerson = React.useCallback(
    () => navigate(toolPersonPath(tool, personId, range, member, personName)),
    [navigate, tool, personId, range, member, personName]
  );

  /* Esc unwinds ONE level, following the trail that is actually on screen.
     (Classic branch only — the graphical branch uses useEscapeBack below,
     which walks backTarget level by level: sitting → day → person → digest.) */
  React.useEffect(() => {
    if (view === "digest" || isGraphicalDrill()) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (day) backToPerson();
      else if (view === "stretch" && personId) backToPerson();
      else backToDigest();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view, personId, day, backToPerson, backToDigest]);

  const openStretch = (i) => navigate(toolStretchPath(tool, i, range, member, personId, personId ? personName : null));
  const goAppUsage = () => navigate(`${DASHBOARD_BASE}/app-usage`);

  const crumbs = [
    { label: "Dashboard", to: classicPath(range, member) },
    view === "digest" ? { label: name } : { label: name, to: toolPath(tool, range, member) },
    personId
      ? (view === "person" && !day
        ? { label: personName }
        : { label: personName, to: toolPersonPath(tool, personId, range, member, personName) })
      : null,
    day ? (sess
      ? { label: day, to: toolPersonPath(tool, personId, range, member, personName, day) }
      : { label: day }) : null,
    sess ? { label: "This sitting" } : null,
    view === "stretch" ? { label: String(stretch?.title || "").replace(/\s+and\s+\d+\s+more\s+pages?$/i, "").trim() || "One sitting" } : null,
  ].filter(Boolean);

  const backTarget = view === "digest"
    ? classicPath(range, member)
    : sess
      ? toolPersonPath(tool, personId, range, member, personName, day)
      : day
        ? toolPersonPath(tool, personId, range, member, personName)
        : view === "stretch" && personId
          ? toolPersonPath(tool, personId, range, member, personName)
          : toolPath(tool, range, member);

  /* Graphical: Escape zooms out exactly one level — the same unwind the back
     button takes (sitting → day → person → digest). Never fires from digest. */
  const escBack = React.useCallback(() => navigate(backTarget), [navigate, backTarget]);
  useEscapeBack(escBack, isGraphicalDrill() && view !== "digest");

  if (isGraphicalDrill()) {
    return (
      <GraphicalTool
        t={t}
        view={view}
        name={name}
        type={type}
        category={category}
        data={data}
        loading={loading}
        people={people}
        coverage={coverage}
        person={person}
        personName={personName}
        personId={personId}
        day={day}
        sess={sess}
        allStretches={allStretches}
        activity={data?.activity || []}
        timeline={data?.timeline || []}
        personStretchIdx={personStretchIdx}
        stretch={stretch}
        focus={focus}
        crumbs={crumbs}
        backTo={backTarget}
        backLabel={view === "digest" ? "Dashboard" : "Back"}
        subject={day ? [name, personName, day] : view === "person" ? [name, personName] : view === "stretch" ? [name, personName, stretch?.title] : [name]}
        excludes={view === "digest" ? ["every other application and site"] : ["every other tool", "work outside this tool"]}
        openStretch={openStretch}
        openPerson={(id, pn) => navigate(toolPersonPath(tool, id, range, member, pn))}
        openPersonDay={(id, pn, d) => navigate(toolPersonPath(tool, id, range, member, pn, d))}
        openSession={(id, pn, d, sn) => navigate(toolPersonPath(tool, id, range, member, pn, d, sn?.startMs))}
        openPersonFull={() => navigate(personPath(personId, range, member, personName))}
        openAppUsage={goAppUsage}
        onJumpStretch={openStretch}
        onBackStretch={() => navigate(backTarget)}
      />
    );
  }

  return (
    <PageShell
      title={view === "person" ? personName : name}
      subtitle={view === "person"
        ? `In ${name} · ${type === "website" ? "Website" : "Application"}`
        : `${type === "website" ? "Website" : "Application"}${category ? ` · ${category}` : ""}`}
      icon={type === "website" ? <Globe size={16} /> : <AppWindow size={16} />}
      actions={
        <Button variant="secondary" size="sm" leftIcon={<ArrowLeft size={14} />} onClick={() => navigate(backTarget)}>
          {view === "digest" ? "Dashboard" : "Back"}
        </Button>
      }
    >
      <DashAntd>
        <Crumbs><Breadcrumb items={crumbs} /></Crumbs>
        {showingSample && <DemoBanner t={t} what="The written analysis" />}
        {name && (
          <div style={{ marginBottom: t.s[4] }}>
            <ScopeBar
              t={t}
              subject={[name]}
              excludes={["every other application and site"]}
            />
          </div>
        )}

        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : !data || data.empty ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 560 }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ fontSize: 13, color: t.c.textTertiary, lineHeight: 1.6 }}>
                  <b style={{ color: t.c.text }}>
                    {name ? `No per-person detail for ${name} in this window.` : "No tool in this link."}
                  </b><br />
                  {name
                    ? "Its time was captured without per-tool breakdown — the totals on the dashboard are still real."
                    : "The address is missing the tool it should open. Go back and click a tool from the dashboard."}
                  <br /><DemoHint t={t} />
                </span>
              }
            />
            <AntButton block onClick={goAppUsage}>Open App usage ›</AntButton>
          </div>
        ) : view === "stretch" ? (
          stretch ? (
            <div style={{ maxWidth: 860 }}>
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
                <Chip t={t} tone="ai">{stretch.userName}</Chip>
                {!stretch.mentions && <Chip t={t} tone="warn">same-day context — does not name {name}</Chip>}
              </div>
              <StretchAnalysis
                userId={stretch?.userId || null}
                stretch={stretch}
                index={focus}
                total={allStretches.length}
                timeline={null}
                onBack={() => navigate(backTarget)}
                onJump={openStretch}
                /* Stays on topic: the "full log" from inside a tool is this
                   person's work IN THIS TOOL, not their whole week. */
                onFullLog={() => navigate(
                  toolPersonPath(tool, stretch.userId, range, member, stretch.userName)
                )}
              />
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`That stretch is no longer in this window for ${name}.`} />
          )
        ) : view === "person" ? (
          /* ── one person, still inside the tool ──────────────────────────── */
          <div style={{ maxWidth: 860, display: "flex", flexDirection: "column", gap: t.s[5] }}>
            <div style={{
              display: "grid", gridTemplateColumns: "1fr auto", gap: 14, alignItems: "center",
              padding: "14px 16px", borderRadius: t.r.md, boxShadow: `0 0 0 1px ${t.c.border}`, background: t.c.surface,
            }}>
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: t.c.textTertiary }}>
                  {personName} in {name}
                </div>
                <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.9px", marginTop: 7, lineHeight: 1, fontVariantNumeric: "tabular-nums", color: t.c.text }}>
                  {person ? fmtMins(person.minutes) : "—"}
                </div>
                {person?.position && <div style={{ fontSize: 11.5, color: t.c.textTertiary, marginTop: 6 }}>{person.position}</div>}
              </div>
              <div style={{ textAlign: "right", fontSize: 12, color: t.c.textTertiary }}>
                Of {fmtMins(data.totalMinutes)}<br />
                <b style={{ fontSize: 17, color: t.c.text, fontVariantNumeric: "tabular-nums" }}>
                  {data.totalMinutes ? Math.round(((person?.minutes || 0) / data.totalMinutes) * 100) : 0}%
                </b>
              </div>
            </div>

            {personStretchIdx.length > 0 ? (
              <Section t={t} title={`What ${personName.split(" ")[0]} did in ${name}`} hint="click a row to open its analysis">
                {personStretchIdx.map((i) => (
                  <ToolStretch key={i} t={t} st={allStretches[i]} onOpen={() => openStretch(i)} />
                ))}
              </Section>
            ) : (
              <Alert
                type="info" showIcon style={{ fontSize: 12.5 }}
                message={`No written analysis names ${name} for ${personName} in this window`}
                description={
                  <>
                    Their {person ? fmtMins(person.minutes) : "time"} in {name} is real. The written layer
                    either has not run for them in this period, or their analysis did not mention this tool.
                    <br /><DemoHint t={t} />
                  </>
                }
              />
            )}

            {/* The ONLY way out of the topic, and it says so. */}
            <div>
              <button
                onClick={() => navigate(personPath(personId, range, member, personName))}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none",
                  padding: 0, cursor: "pointer", fontFamily: t.font.family,
                  fontSize: 12.5, fontWeight: 600, color: t.c.primaryHover,
                }}
              >
                Open {personName.split(" ")[0]}’s full drill-down <ExternalLink size={13} />
              </button>
              <div style={{ fontSize: 11, color: t.c.textTertiary, marginTop: 4 }}>
                Leaves {name} — that page covers all of their work in this window.
              </div>
            </div>
          </div>
        ) : (
          /* ── the tool digest ────────────────────────────────────────────── */
          <div className="tg-drill-grid" style={{ display: "grid", gap: t.s[5], alignItems: "start" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: t.s[5], minWidth: 0 }}>
              <div style={{
                display: "grid", gridTemplateColumns: "1fr auto", gap: 14, alignItems: "start",
                padding: "14px 16px", borderRadius: t.r.md, boxShadow: `0 0 0 1px ${t.c.border}`, background: t.c.surface,
              }}>
                <div>
                  <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: t.c.textTertiary }}>
                    Time in {name}
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.9px", marginTop: 7, lineHeight: 1, fontVariantNumeric: "tabular-nums", color: t.c.text }}>
                    {fmtMins(data.totalMinutes)}
                  </div>
                  <Spark values={data.sparkMinutes || []} tip="Use by day across the window" />
                </div>
                <div style={{ textAlign: "right", fontSize: 12, color: t.c.textTertiary }}>
                  People<br />
                  <b style={{ fontSize: 17, color: t.c.text, fontVariantNumeric: "tabular-nums" }}>{people.length}</b>
                </div>
              </div>

              {allStretches.length === 0 && (data.insights || []).length === 0 && (
                <Alert
                  type="info" showIcon style={{ fontSize: 12.5 }}
                  message={`No written analysis names ${name} in this window`}
                  description={
                    <>
                      The hours above are real. The written layer either has not run for {name}&apos;s heaviest
                      users in this period, or their analysis did not mention it. Once the summary pipeline
                      covers them, their stretches and hourly analysis appear here automatically.
                      <br /><DemoHint t={t} />
                    </>
                  }
                />
              )}

              {allStretches.length > 0 && (
                <Section t={t} title="What was done in it" hint="click a row to open its analysis">
                  {allStretches.map((st, i) => (
                    <ToolStretch key={i} t={t} st={st} onOpen={() => openStretch(i)} />
                  ))}
                </Section>
              )}

              {(data.insights || []).length > 0 && (
                <Section t={t} title="From the written analysis">
                  {data.insights.map((e, i) => (
                    <div key={i} style={{ padding: "10px 12px", borderRadius: 8, background: t.c.surfaceMuted, marginBottom: 7 }}>
                      {e.title && <div style={{ fontSize: 12.5, fontWeight: 600, color: t.c.text, marginBottom: 3 }}>{e.title}</div>}
                      <div style={{ fontSize: 12.5, lineHeight: 1.6, color: t.c.textSecondary }}><Prose text={e.text} compact /></div>
                      <div style={{ marginTop: 5, fontSize: 10.5, color: t.c.textTertiary }}>
                        {[e.userName, e.day, e.hourSlot].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  ))}
                </Section>
              )}
            </div>

            <aside className="tg-drill-rail" style={{ display: "flex", flexDirection: "column", gap: t.s[5], minWidth: 0, position: "sticky", top: 0 }}>
              <Section t={t} title="Who used it" hint={`click anyone for their time in ${name}`}>
                {people.map((p) => (
                  <button
                    key={p.userId}
                    onClick={() => navigate(toolPersonPath(tool, p.userId, range, member, p.name))}
                    style={{
                      display: "grid", gridTemplateColumns: "1fr auto auto auto", alignItems: "center", gap: 10,
                      width: "100%", padding: "9px 10px", margin: "0 -10px", borderRadius: t.r.sm,
                      border: "none", background: "none", textAlign: "left", cursor: "pointer", fontFamily: t.font.family,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = t.c.surfaceHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
                  >
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13, fontWeight: 500, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                      {p.position && <span style={{ display: "block", fontSize: 11, color: t.c.textTertiary }}>{p.position}</span>}
                    </span>
                    <TrackBar pct={(p.minutes / maxPerson) * 100} />
                    <span style={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: t.c.text, width: 56, textAlign: "right" }}>{fmtH(p.minutes)}</span>
                    <ChevronRight size={14} style={{ color: t.c.textTertiary }} />
                  </button>
                ))}
              </Section>

              {coverage && coverage.covered < coverage.total && (
                <Alert
                  type="info" showIcon style={{ fontSize: 12 }}
                  message={`Based on activity detail from ${coverage.covered} of ${coverage.total} people`}
                  description="Time from the rest is counted in the dashboard totals but carries no per-tool breakdown."
                />
              )}

              <AntButton block onClick={goAppUsage}>Open App usage ›</AntButton>
            </aside>
          </div>
        )}
      </DashAntd>
    </PageShell>
  );
}

/** One tool stretch: a compact takeaway row that opens its own analysis. */
function ToolStretch({ t, st, onOpen }) {
  return (
    <button
      onClick={onOpen}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, width: "100%",
        border: "none", background: t.c.surface, boxShadow: `0 0 0 1px ${t.c.border}`,
        borderRadius: 9, padding: "10px 12px", marginBottom: 8, textAlign: "left", cursor: "pointer", fontFamily: t.font.family,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 1px ${t.c.primaryRing}, 0 3px 10px ${t.c.shadowColor}`; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = `0 0 0 1px ${t.c.border}`; }}
    >
      <span style={{ minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: t.c.text, lineHeight: 1.35 }}>{st.title}</span>
        <span style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
          <Chip t={t} tone="dur">{st.durationLabel}</Chip>
          <Chip t={t} tone="ai">{st.userName}</Chip>
          {st.project && <Chip t={t}>{st.project}</Chip>}
          <span style={{ fontSize: 10.5, color: t.c.textTertiary }}>{st.day}{st.t0 ? ` · ${st.t0}` : ""}</span>
          {!st.mentions && <Chip t={t} tone="warn">same-day context</Chip>}
        </span>
      </span>
      <span style={{ color: t.c.textTertiary, fontSize: 15 }}>›</span>
    </button>
  );
}

function Chip({ t, tone, children }) {
  const map = {
    dur: { bg: t.c.primarySoft, fg: t.c.primary },
    ai: { bg: t.c.aiSoft, fg: t.c.ai },
    warn: { bg: t.status.warningSoft, fg: t.status.warning },
  };
  const c = map[tone] || { bg: t.c.surfaceMuted, fg: t.c.textSecondary };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", height: 19, padding: "0 7px", borderRadius: 5,
      fontSize: 10.5, fontWeight: 600, background: c.bg, color: c.fg, whiteSpace: "nowrap",
      maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis",
    }}>{children}</span>
  );
}

export default ToolDrilldown;
